import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc
} from 'firebase/firestore'
import ProtectedRoute from '../../components/ProtectedRoute'
import { db } from '../../firebase'
import { PremiumFeaturePill } from '../../components/premium/PremiumInfoButton'
import {
  PREMIUM_ACCESS_DOC_PATH,
  YEARLY_PREMIUM_PRICE,
  calculatePremiumExpiryDate,
  calculatePremiumMonthsFromAmount,
  createPremiumEntry,
  formatPremiumDate,
  formatPremiumPrice,
  isPremiumEntryActive,
  isPremiumEntryExpired,
  isValidPremiumEmail,
  normalizePremiumEntries,
  normalizePremiumEmail,
  upsertPremiumEntry
} from '../../utils/premiumAccess'

const getUserLabel = user =>
  user?.username || user?.displayName || user?.email || user?.id || ''

const padDatePart = value => String(value).padStart(2, '0')

const formatDateInput = value => {
  const date = value instanceof Date ? value : new Date(value)
  if (!date || Number.isNaN(date.getTime())) return ''

  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate())
  ].join('-')
}

const parseDateInput = value => {
  const [year, month, day] = String(value || '').split('-').map(Number)
  if (!year || !month || !day) return null

  const date = new Date(year, month - 1, day)
  return Number.isNaN(date.getTime()) ? null : date
}

const parseExpiryInput = value => {
  const date = parseDateInput(value)
  if (!date) return null
  date.setHours(23, 59, 59, 999)
  return date
}

const getExpiryInputForAmount = (amount, startDateInput) => {
  const startDate = parseDateInput(startDateInput) || new Date()
  const expiryDate = calculatePremiumExpiryDate({ amount, startDate })
  return expiryDate ? formatDateInput(expiryDate) : ''
}

const getDefaultForm = () => {
  const startDate = new Date()
  const startDateInput = formatDateInput(startDate)

  return {
    email: '',
    uid: '',
    label: '',
    paidAmount: YEARLY_PREMIUM_PRICE,
    startDate: startDateInput,
    expiresAt: getExpiryInputForAmount(YEARLY_PREMIUM_PRICE, startDateInput)
  }
}

export default function PremiumUsers () {
  const [entries, setEntries] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [form, setForm] = useState(() => getDefaultForm())

  const activeCount = useMemo(
    () => entries.filter(entry => isPremiumEntryActive(entry)).length,
    [entries]
  )
  const calculatedMonths = useMemo(
    () => calculatePremiumMonthsFromAmount(form.paidAmount),
    [form.paidAmount]
  )

  const normalizedMessage = message.toLowerCase()
  const hasDangerMessage =
    normalizedMessage.includes('errore') ||
    normalizedMessage.includes('non corrisponde')
  const messageTone = hasDangerMessage
    ? 'danger'
    : normalizedMessage.startsWith('inserisci')
      ? 'warning'
      : 'success'

  const sortedEntries = useMemo(
    () =>
      [...entries].sort(
        (a, b) =>
          Number(isPremiumEntryActive(b)) - Number(isPremiumEntryActive(a)) ||
          Number(isPremiumEntryExpired(a)) - Number(isPremiumEntryExpired(b)) ||
          (a.label || a.email || a.uid).localeCompare(
            b.label || b.email || b.uid,
            'it'
          )
      ),
    [entries]
  )

  const loadData = useCallback(async () => {
    setLoading(true)
    setMessage('')

    try {
      const [premiumSnap, usersSnap] = await Promise.all([
        getDoc(doc(db, ...PREMIUM_ACCESS_DOC_PATH)),
        getDocs(collection(db, 'users'))
      ])
      const premiumData = premiumSnap.exists() ? premiumSnap.data() : {}
      const userList = usersSnap.docs
        .map(userDoc => ({ id: userDoc.id, ...userDoc.data() }))
        .sort((a, b) => getUserLabel(a).localeCompare(getUserLabel(b), 'it'))

      setEntries(normalizePremiumEntries(premiumData.users))
      setUsers(userList)
    } catch (error) {
      console.error('Errore caricamento utenti premium:', error)
      setMessage('Errore durante il caricamento degli utenti premium.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const saveEntries = async nextEntries => {
    setSaving(true)
    setMessage('')

    try {
      const normalizedEntries = normalizePremiumEntries(nextEntries)
      await setDoc(
        doc(db, ...PREMIUM_ACCESS_DOC_PATH),
        {
          users: normalizedEntries,
          updatedAt: serverTimestamp()
        },
        { merge: true }
      )
      setEntries(normalizedEntries)
      setMessage('Lista Premium aggiornata.')
    } catch (error) {
      console.error('Errore salvataggio premium:', error)
      setMessage('Errore durante il salvataggio della lista Premium.')
    } finally {
      setSaving(false)
    }
  }

  const findExistingEntryForUser = user => entries.find(entry =>
    (user?.id && entry.uid === user.id) ||
    (user?.email && entry.email === normalizePremiumEmail(user.email))
  )

  const findUserByEmail = email => {
    const normalizedEmail = normalizePremiumEmail(email)
    if (!normalizedEmail) return null

    return users.find(user => normalizePremiumEmail(user.email) === normalizedEmail) || null
  }

  const getRenewalStartDate = entry => {
    const today = new Date()
    const expiresAt = entry?.expiresAt ? new Date(entry.expiresAt) : null

    if (expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt > today) {
      return expiresAt
    }

    return today
  }

  const handleSelectUser = userId => {
    const selectedUser = users.find(user => user.id === userId)
    if (!selectedUser) {
      setForm(prev => ({ ...prev, uid: '' }))
      return
    }

    const existingEntry = findExistingEntryForUser(selectedUser)
    const startDate = getRenewalStartDate(existingEntry)
    const startDateInput = formatDateInput(startDate)

    setForm({
      email: selectedUser.email || '',
      uid: selectedUser.id,
      label: getUserLabel(selectedUser),
      paidAmount: form.paidAmount,
      startDate: startDateInput,
      expiresAt: getExpiryInputForAmount(form.paidAmount, startDateInput)
    })
  }

  const handlePaidAmountChange = amount => {
    setForm(prev => ({
      ...prev,
      paidAmount: amount,
      expiresAt: getExpiryInputForAmount(amount, prev.startDate)
    }))
  }

  const handleStartDateChange = startDate => {
    setForm(prev => ({
      ...prev,
      startDate,
      expiresAt: getExpiryInputForAmount(prev.paidAmount, startDate)
    }))
  }

  const handleAddEntry = async event => {
    event.preventDefault()

    if (calculatedMonths <= 0) {
      setMessage('Inserisci un importo valido per calcolare la scadenza.')
      return
    }

    const normalizedEmail = normalizePremiumEmail(form.email)
    const selectedUser = form.uid
      ? users.find(user => user.id === form.uid) || null
      : null
    const selectedUserEmail = normalizePremiumEmail(selectedUser?.email)

    if (normalizedEmail && !isValidPremiumEmail(normalizedEmail)) {
      setMessage('Inserisci una email valida.')
      return
    }

    if (
      selectedUser &&
      selectedUserEmail &&
      normalizedEmail &&
      selectedUserEmail !== normalizedEmail
    ) {
      setMessage("La mail inserita non corrisponde all'utente selezionato.")
      return
    }

    const matchedUserByEmail = normalizedEmail
      ? findUserByEmail(normalizedEmail)
      : null
    const resolvedUser = selectedUser || matchedUserByEmail
    const resolvedForm = {
      ...form,
      email: normalizedEmail || normalizePremiumEmail(resolvedUser?.email),
      uid: form.uid || resolvedUser?.id || '',
      label: String(form.label || '').trim() || getUserLabel(resolvedUser)
    }
    const matchingEntry = entries.find(entry =>
      (resolvedForm.uid && entry.uid === resolvedForm.uid) ||
      (resolvedForm.email && entry.email === resolvedForm.email)
    )
    const nextEntry = createPremiumEntry({
      ...resolvedForm,
      paidMonths: calculatedMonths,
      expiresAt: parseExpiryInput(form.expiresAt),
      createdAt: matchingEntry?.createdAt || new Date().toISOString()
    })

    if (!nextEntry.email && !nextEntry.uid) {
      setMessage('Inserisci almeno una email o seleziona un utente.')
      return
    }

    await saveEntries(upsertPremiumEntry(entries, nextEntry))
    setForm(getDefaultForm())
  }

  const toggleEntry = async entry => {
    await saveEntries(
      entries.map(item =>
        item.id === entry.id ? { ...item, active: item.active === false } : item
      )
    )
  }

  const removeEntry = async entry => {
    if (!window.confirm(`Rimuovere ${entry.email || entry.label || entry.uid} dal Premium?`)) {
      return
    }

    await saveEntries(entries.filter(item => item.id !== entry.id))
  }

  return (
    <ProtectedRoute>
      <div className='container admin-page py-4'>
        <div className='admin-page-heading mb-4'>
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'>
            <div>
              <h2 className='mb-1 d-flex align-items-center gap-2'>
                <i className='bi bi-stars text-brand-secondary'></i>
                Utenti Premium
              </h2>
              <p className='mb-0 text-muted'>
                Gestisci mail e utenti abilitati al pacchetto Premium.
              </p>
            </div>
            <PremiumFeaturePill label={`${activeCount} attivi`} />
          </div>
        </div>

        {message && (
          <div
            className={`alert alert-${messageTone} py-2`}
          >
            {message}
          </div>
        )}

        <div className='row g-4'>
          <div className='col-12 col-lg-4'>
            <form className='card shadow-sm admin-list-card' onSubmit={handleAddEntry}>
              <div className='card-header bg-light py-3'>
                <h5 className='mb-0'>
                  <i className='bi bi-person-plus me-2'></i>
                  Aggiungi Premium
                </h5>
              </div>
              <div className='card-body d-grid gap-3'>
                <div>
                  <label className='form-label'>Utente registrato</label>
                  <select
                    className='form-select'
                    value={form.uid}
                    onChange={event => handleSelectUser(event.target.value)}
                    disabled={saving}
                  >
                    <option value=''>Scegli utente...</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {getUserLabel(user)} {user.email ? `- ${user.email}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className='form-label'>Email</label>
                  <input
                    type='email'
                    className='form-control'
                    value={form.email}
                    onChange={event =>
                      setForm(prev => ({ ...prev, email: event.target.value }))
                    }
                    placeholder='utente@email.com'
                    disabled={saving}
                  />
                </div>

                <div>
                  <label className='form-label'>Nome visibile</label>
                  <input
                    type='text'
                    className='form-control'
                    value={form.label}
                    onChange={event =>
                      setForm(prev => ({ ...prev, label: event.target.value }))
                    }
                    placeholder='Nome o username'
                    disabled={saving}
                  />
                </div>

                <div>
                  <label className='form-label'>Importo pagato</label>
                  <div className='input-group'>
                    <span className='input-group-text'>€</span>
                    <input
                      type='number'
                      min='0.5'
                      step='0.5'
                      className='form-control'
                      value={form.paidAmount}
                      onChange={event => handlePaidAmountChange(event.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <small className='text-muted d-block mt-1'>
                    {calculatedMonths > 0
                      ? `${calculatedMonths} ${calculatedMonths === 1 ? 'mese' : 'mesi'} Premium`
                      : 'Importo non valido'}
                  </small>
                </div>

                <div>
                  <label className='form-label'>Decorrenza</label>
                  <input
                    type='date'
                    className='form-control'
                    value={form.startDate}
                    onChange={event => handleStartDateChange(event.target.value)}
                    disabled={saving}
                  />
                  <small className='text-muted d-block mt-1'>
                    Se l&apos;utente e gia Premium, parte dalla scadenza attuale.
                  </small>
                </div>

                <div>
                  <label className='form-label'>Scadenza Premium</label>
                  <input
                    type='date'
                    className='form-control'
                    value={form.expiresAt}
                    onChange={event =>
                      setForm(prev => ({ ...prev, expiresAt: event.target.value }))
                    }
                    disabled={saving}
                  />
                  <small className='text-muted d-block mt-1'>
                    Calcolata automaticamente da {formatPremiumPrice(form.paidAmount)}.
                  </small>
                </div>

                <button
                  type='submit'
                  className='btn btn-brand-secondary'
                  disabled={saving || loading}
                >
                  {saving ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2'></span>
                      Salvataggio...
                    </>
                  ) : (
                    <>
                      <i className='bi bi-stars me-1'></i>
                      Abilita Premium
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className='col-12 col-lg-8'>
            <div className='card shadow-sm admin-list-card'>
              <div className='card-header bg-light py-3 d-flex justify-content-between align-items-center gap-2'>
                <h5 className='mb-0'>
                  <i className='bi bi-list-stars me-2'></i>
                  Lista Premium ({entries.length})
                </h5>
                <button
                  type='button'
                  className='btn btn-sm btn-outline-secondary'
                  onClick={loadData}
                  disabled={saving || loading}
                >
                  <i className='bi bi-arrow-clockwise me-1'></i>
                  Ricarica
                </button>
              </div>
              <div className='card-body'>
                {loading ? (
                  <div className='text-center py-4'>
                    <span className='spinner-border text-primary'></span>
                  </div>
                ) : sortedEntries.length === 0 ? (
                  <div className='text-center py-5 text-muted'>
                    <i className='bi bi-stars fs-1 d-block mb-2'></i>
                    Nessun utente Premium configurato.
                  </div>
                ) : (
                  <div className='table-responsive'>
                    <table className='table table-hover align-middle mb-0'>
                      <thead className='table-light'>
                        <tr>
                          <th>Utente</th>
                          <th>Email</th>
                          <th>Scadenza</th>
                          <th>Pagamento</th>
                          <th>UID</th>
                          <th className='text-end'>Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedEntries.map(entry => {
                          const expired = isPremiumEntryExpired(entry)
                          const active = isPremiumEntryActive(entry)
                          const statusClassName = expired
                            ? 'bg-danger'
                            : active
                              ? 'bg-success'
                              : 'bg-secondary'
                          const statusLabel = expired
                            ? 'Scaduto'
                            : active
                              ? 'Attivo'
                              : 'Disattivo'

                          return (
                            <tr key={entry.id}>
                              <td>
                                <div className='fw-semibold'>
                                  {entry.label || entry.email || entry.uid}
                                </div>
                                <span className={`badge ${statusClassName}`}>
                                  {statusLabel}
                                </span>
                              </td>
                              <td>{entry.email || '-'}</td>
                              <td>
                                <div className={expired ? 'fw-semibold text-danger' : 'fw-semibold'}>
                                  {formatPremiumDate(entry.expiresAt)}
                                </div>
                                {entry.paidMonths > 0 && (
                                  <small className='text-muted'>
                                    {entry.paidMonths} {entry.paidMonths === 1 ? 'mese' : 'mesi'}
                                  </small>
                                )}
                              </td>
                              <td>
                                {entry.paidAmount > 0
                                  ? formatPremiumPrice(entry.paidAmount)
                                  : '-'}
                              </td>
                              <td className='small text-muted'>{entry.uid || '-'}</td>
                              <td>
                                <div className='d-flex justify-content-end gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-outline-warning'
                                    onClick={() => toggleEntry(entry)}
                                    disabled={saving}
                                    title={entry.active !== false ? 'Disattiva' : 'Attiva'}
                                  >
                                    <i
                                      className={`bi ${
                                        entry.active !== false
                                          ? 'bi-pause-fill'
                                          : 'bi-play-fill'
                                      }`}
                                    ></i>
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-outline-danger'
                                    onClick={() => removeEntry(entry)}
                                    disabled={saving}
                                    title='Rimuovi'
                                  >
                                    <i className='bi bi-trash'></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  )
}
